import React, { useState, useMemo, useCallback } from 'react'
import {
  useWatch,
  Control,
  FormState,
  TriggerConfig,
  FieldPath
} from 'react-hook-form'
import { useBoolean } from 'react-use'

import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { EndpointResponse } from 'types/api/status-checks/status-check'
import { isValidUrl } from 'utils/strings'

export interface StatusCheckDetailsContextValues {
  endpointResponse: EndpointResponse
  handleSuccess: (endpointResponse: EndpointResponse) => Promise<void>
  isStatusCheckConnectionValidated: boolean
  isStatusCheckEvaluationValidated: boolean
  isTestSuccessEvaluationDisabled: boolean
  serviceName?: string
  setIsStatusCheckConnectionValidated: (arg: boolean) => void
  setIsStatusCheckEvaluationValidated: (arg: boolean) => void
  setEndpointResponse: (endpointResponse: EndpointResponse) => void
  shouldDisplayPredicateConfiguration: boolean
  handleTestConnectionError: () => void
  isTestStatusCheckDisabled: boolean
}

export const DEFAULT_STATUS_CHECK_DETAILS_VALUES: StatusCheckDetailsContextValues =
  {
    endpointResponse: {},
    handleSuccess: () => new Promise(() => {}),
    handleTestConnectionError: () => {},
    isStatusCheckConnectionValidated: false,
    isStatusCheckEvaluationValidated: false,
    isTestStatusCheckDisabled: false,
    isTestSuccessEvaluationDisabled: false,
    serviceName: '',
    setEndpointResponse: () => {},
    setIsStatusCheckConnectionValidated: () => {},
    setIsStatusCheckEvaluationValidated: () => {},
    shouldDisplayPredicateConfiguration: false
  }
export const StatusCheckDetailsContext =
  React.createContext<StatusCheckDetailsContextValues>(
    DEFAULT_STATUS_CHECK_DETAILS_VALUES
  )

export const StatusCheckDetailsProvider: React.FC<{
  serviceName: string
  control: Control<StatusCheckFormValues>
  formState: FormState<StatusCheckFormValues>
  setValue: (statusCheckName: string, resName: string) => void
  trigger: (
    name?:
      | FieldPath<StatusCheckFormValues>
      | FieldPath<StatusCheckFormValues>[],
    options?: TriggerConfig
  ) => Promise<boolean>
  children
}> = (props) => {
  const [
    isStatusCheckEvaluationValidated,
    setIsStatusCheckEvaluationValidated
  ] = useBoolean(false)

  const [
    isStatusCheckConnectionValidated,
    setIsStatusCheckConnectionValidated
  ] = useBoolean(false)

  const [endpointResponse, setEndpointResponse] = useState<EndpointResponse>()

  const { children, control, formState, serviceName, setValue, trigger } = props

  const [
    monitorUrl,
    evaluationConfiguration,
    externalIntegration,
    statusCheckName
  ] = useWatch<StatusCheckFormValues>({
    control,
    name: [
      'monitorUrl',
      'evaluationConfiguration',
      'externalIntegration',
      'statusCheckName'
    ]
  })

  const handleSuccess = useCallback(
    async (endpointResponse) => {
      setEndpointResponse(endpointResponse)

      setIsStatusCheckConnectionValidated(true)

      // Pull the resource name from the response body
      const resName = getResourceNameFromResponseBody(
        endpointResponse,
        externalIntegration
      )

      // don't overwrite the status check name from the response body if it exists
      // in the form
      if (resName && !statusCheckName) {
        setValue('statusCheckName', resName)
      }

      // Validate monitorUrl on test success
      await trigger('monitorUrl')
    },
    [
      externalIntegration,
      setIsStatusCheckConnectionValidated,
      setValue,
      statusCheckName,
      trigger
    ]
  )

  const handleTestConnectionError = useCallback(() => {
    if (isStatusCheckConnectionValidated) {
      setIsStatusCheckConnectionValidated(false)
    }
  }, [isStatusCheckConnectionValidated, setIsStatusCheckConnectionValidated])

  const isTestStatusCheckDisabled = useMemo(() => {
    const isDisabled =
      Boolean(formState.errors.monitorUrl) || !isValidUrl(monitorUrl)

    return isDisabled
  }, [formState.errors.monitorUrl, monitorUrl])

  const isTestSuccessEvaluationDisabled = useMemo(() => {
    return (
      isTestStatusCheckDisabled ||
      !isStatusCheckConnectionValidated ||
      !endpointResponse
    )
  }, [
    endpointResponse,
    isStatusCheckConnectionValidated,
    isTestStatusCheckDisabled
  ])

  const shouldDisplayPredicateConfiguration = useMemo<boolean>(() => {
    const endpointResponseContentType = endpointResponse?.contentType
    const predicates =
      evaluationConfiguration?.responseBodyEvaluation?.predicates

    const isJson =
      String(endpointResponseContentType).toLowerCase().indexOf('json') !== -1

    return isJson || predicates?.length > 0
  }, [
    endpointResponse?.contentType,
    evaluationConfiguration?.responseBodyEvaluation?.predicates
  ])

  const value = useMemo(() => {
    return {
      endpointResponse,
      handleSuccess,
      handleTestConnectionError,
      isStatusCheckConnectionValidated,
      isStatusCheckEvaluationValidated,
      isTestStatusCheckDisabled,
      isTestSuccessEvaluationDisabled,
      serviceName,
      setEndpointResponse,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated,
      shouldDisplayPredicateConfiguration
    }
  }, [
    endpointResponse,
    handleSuccess,
    handleTestConnectionError,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    isTestSuccessEvaluationDisabled,
    serviceName,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated,
    shouldDisplayPredicateConfiguration
  ])

  return (
    <StatusCheckDetailsContext.Provider value={value}>
      {children}
    </StatusCheckDetailsContext.Provider>
  )
}
